import { useState, useEffect, useCallback, useMemo } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../App";
import { toast } from "sonner";
import { Calendar } from "../../components/ui/calendar";
import { Loader2, Truck, ClipboardCheck } from "lucide-react";

const MODIFIERS_CLASS_NAMES = { hasEvent: "bg-[#FF5A1F]/20 font-bold" };

const AdminCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/calendar");
      setEvents(response.data.events);
    } catch {
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const datesWithEvents = useMemo(() => events.map(event => new Date(event.date)), [events]);
  const calendarModifiers = useMemo(() => ({ hasEvent: datesWithEvents }), [datesWithEvents]);

  const selectedDateEvents = getEventsForDate(selectedDate);

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-500",
      estimated: "bg-yellow-500",
      survey_required: "bg-orange-500",
      quoted: "bg-purple-500",
      deposit_paid: "bg-green-500",
      confirmed: "bg-green-500",
      completed: "bg-slate-500",
      cancelled: "bg-red-500",
      requested: "bg-yellow-500"
    };
    return colors[status] || "bg-slate-500";
  };

  return (
    <AdminLayout title="Calendar">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" />
              </div>
            ) : (
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                modifiers={calendarModifiers}
                modifiersClassNames={MODIFIERS_CLASS_NAMES}
                className="rounded-md border"
              />
            )}
          </div>
        </div>

        {/* Events for Selected Date */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-['Outfit'] font-bold text-[#0F172A]">
                {selectedDate.toLocaleDateString('en-GB', { 
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </h2>
            </div>
            
            <div className="p-4">
              {selectedDateEvents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No events scheduled</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedDateEvents.map((event) => (
                    <div 
                      key={event.id || `${event.type}-${event.time}`}
                      className="border border-slate-200 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 ${event.type === 'move' ? 'bg-[#FF5A1F]' : 'bg-blue-500'} flex items-center justify-center flex-shrink-0`}>
                          {event.type === 'move' ? (
                            <Truck className="w-5 h-5 text-white" />
                          ) : (
                            <ClipboardCheck className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-[#0F172A] truncate">
                              {event.title}
                            </span>
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(event.status)}`} />
                          </div>
                          {event.details && (
                            <div className="text-sm text-slate-500 mt-1">
                              {event.details}
                            </div>
                          )}
                          <div className="text-xs text-slate-400 mt-1 capitalize">
                            {event.status?.replace('_', ' ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="bg-white border border-slate-200 mt-6 p-4">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Legend</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#FF5A1F]"></div>
                <span className="text-sm text-slate-600">Move</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500"></div>
                <span className="text-sm text-slate-600">Survey</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCalendar;
